import React, { useContext, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel } from '@material-ui/core';

import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

import { CircularProgressOverlay } from './CircularProgressOverlay';
import { SkuTabExpansion } from './skuTabExpansion';
import { useInventorySearchValues } from './useInventorySearchValues';

const warehouseInventoryV2Api = new WarehouseInventoryV2Api();

function SkuTab({ onLinkClicked, queryParams }) {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [loadingSkus, setLoadingSkus] = useState(false);
    const [viewActions, setViewActions] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const user = useContext(UserContext);
    const [displayedType, setDisplayedType] = useState({ items: true, packaging: true });
    const [filteredRows, setFilteredRows] = useState([]);
    const searchValues = useInventorySearchValues(queryParams);

    // search bar queryParams effect
    useEffect(() => {
        handleSearch();
    }, [queryParams]);

    useEffect(() => {
        setFilteredRows(rows);
    }, [rows]);

    useEffect(() => {
        if (displayedType.items && !displayedType.packaging) {
            setFilteredRows(rows.filter((row) => row.type !== 'PACKAGING'));
        } else if (!displayedType.items && displayedType.packaging) {
            setFilteredRows(rows.filter((row) => row.type !== 'ITEM'));
        } else if (!displayedType.items && !displayedType.packaging) {
            setFilteredRows([]);
        } else {
            setFilteredRows(rows);
        }
    }, [displayedType]);

    const handleSearch = async () => {
        getSkus();
    };

    // Column Definitions
    const columns = [
        { Header: 'External SKU', accessor: 'externalSku' },
        { Header: 'Partner SKU', accessor: 'partnerSku' },
        { Header: 'Item Name', accessor: 'itemName' },
        { Header: 'Pallets', accessor: 'numPallets' },
        { Header: 'Cases', accessor: 'numCases' },
        { Header: 'Loose Units', accessor: 'numLooseUnits' },
        { Header: 'Total Units', accessor: 'numTotalUnits' },
    ];

    // API call to GET data
    const getSkus = async () => {
        setLoadingSkus(true);
        const result = await warehouseInventoryV2Api.getSkus(user.getFacilityID(), searchValues);
        if (result.status) {
            setRows(
                result.data.skus.map((sku) => ({
                    ...sku,
                })),
            );
        } else {
            setNotification({ text: result.msg, level: 'error' });
        }
        setLoadingSkus(false);
    };

    const handleOnRowClicked = (row) => {
        setSelectedRow(row.original);
    };

    const handleSuccess = () => {
        setNotification({ text: 'Success', level: 'success' });
        getSkus();
    };

    const handleDisplayTypeChange = (event) => {
        setDisplayedType({ ...displayedType, [event.target.name]: event.target.checked });
    };

    return (
        <>
            <Notifications options={notification} />
            <CircularProgressOverlay loading={loadingSkus} />
            <>
                <FormControlLabel
                    control={<Checkbox checked={displayedType.items} onChange={handleDisplayTypeChange} name="items" />}
                    label="Finished Goods"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={displayedType.packaging}
                            onChange={handleDisplayTypeChange}
                            name="packaging"
                        />
                    }
                    label="Packaging"
                />
                <DataTable
                    csvExport
                    csvExportFileName="sku_inventory"
                    columns={columns}
                    data={filteredRows}
                    editable={false}
                    tableContainerMaxHeight={800}
                    initialRowsPerPage={100}
                    stickyHeader
                    expandable
                    ExpansionComponent={
                        <SkuTabExpansion
                            searchValues={searchValues}
                            externalSku={selectedRow?.externalSku}
                            onSuccess={handleSuccess}
                            type={selectedRow?.type}
                            onLinkClicked={onLinkClicked}
                        />
                    }
                    onRowClicked={handleOnRowClicked}
                />
            </>
        </>
    );
}

export { SkuTab };
